import { Component, h, State, Method, Event, EventEmitter, Prop } from '@stencil/core';
import { isBreakpointSmaller } from '../../../utils';

// Add this interface near the top of the file
export interface BreadcrumbItem {
  label?: string;
  isCurrent: boolean;
  isRoot: boolean;
  level?: number;
}

@Component({
  tag: 'dc-ui-header-nav-breadcrumb',
  styleUrl: 'dc-ui-header-nav-breadcrumb.scss',
  shadow: true,
})
export class DcUiHeaderNavBreadcrumb {
  @Prop() menuName: string = 'Menu';

  @State() items: BreadcrumbItem[] = [];
  @State() isMobile: boolean = this.checkIfMobile();

  // Add event emitter for click events
  @Event() breadcrumbClick: EventEmitter<{ index: number; label: string; isRoot: boolean }>;

  private checkIfMobile(): boolean {
    return isBreakpointSmaller('xl');
  }

  componentDidLoad() {
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  private handleResize = () => {
    this.isMobile = this.checkIfMobile();
  };

  private handleClick = (index: number, label: string, isRoot: boolean) => {
    this.breadcrumbClick.emit({ index, label, isRoot });
  };

  @Method()
  async addItem(label?: string, isRoot: boolean = false, level?: number) {
    // On mobile, preserve the root item by keeping it and only modifying items after it
    if (this.isMobile) {
      // Check for duplicate item (same label and level)
      const isDuplicate = this.items.some(
        (item) => item.label === (isRoot ? this.menuName : label) && item.level === level
      );
      if (isDuplicate) return;

      const rootItem = this.items.find((item) => item.isRoot);
      const nonRootItems = this.items.filter((item) => !item.isRoot);

      if (isRoot) {
        // For root items in mobile, just add them if they don't exist
        if (!rootItem) {
          this.items = [
            {
              label: this.menuName,
              isCurrent: false,
              isRoot: true,
              level: 0,
            },
            ...nonRootItems,
          ];
        }
        return;
      }

      // Rest of the mobile logic for non-root items
      const existingItemIndex = nonRootItems.findIndex((item) => item.level === level);

      if (existingItemIndex !== -1) {
        // Remove all items from this level onwards, but keep the root item
        nonRootItems.splice(existingItemIndex);
      }

      // Remove current flag from previous items
      const updatedNonRootItems = nonRootItems.map((item) => ({
        ...item,
        isCurrent: false,
      }));

      // Add new item as current (if it's not a root item)
      if (!isRoot) {
        const newItem = {
          label,
          isCurrent: false,
          isRoot: false,
          level,
        };

        // Combine root item (if exists) with other items and new item
        this.items = rootItem
          ? [rootItem, ...updatedNonRootItems, newItem]
          : [...updatedNonRootItems, newItem];
      }
    } else {
      // Check for duplicate item (same label and level)
      const isDuplicate = this.items.some((item) => item.label === label && item.level === level);
      if (isDuplicate) return;

      const existingItemIndex = this.items.findIndex((item) => item.level === level);

      if (existingItemIndex !== -1) {
        this.items = this.items.slice(0, existingItemIndex);
      }

      this.items = this.items.map((item) => ({
        ...item,
        isCurrent: false,
      }));

      const isRootItemAlreadyAdded = this.items.some((item) => item.isRoot);
      if (isRootItemAlreadyAdded && isRoot) return;

      this.items = [
        ...this.items,
        {
          label: label,
          isCurrent: isRoot ? false : true,
          isRoot,
          level: level,
        },
      ];
    }
  }

  @Method()
  async addRootItem() {
    // On Mobile we need a "special" root item on the first level to go back to the main nav. Default label is "Menu"
    this.addItem(this.menuName, true, 0);
  }

  @Method()
  async removeItem(index: number) {
    if (index < 0 || index >= this.items.length) return;

    const newItems = this.items.filter((_, i) => i !== index);

    // Make the last item current
    if (newItems.length > 0) {
      newItems[newItems.length - 1].isCurrent = true;
    }

    this.items = newItems;
  }

  @Method()
  async removeAllItemsFrom(index: number) {
    // On desktop, if index is 1, clear all items (including root)
    if (!this.isMobile && index === 1) {
      this.items = [];
      return;
    }

    // Adjust index to preserve root item on mobile
    const startIndex = this.isMobile ? Math.max(1, index) : index;

    // Keep only items before the start index
    this.items = this.items.slice(0, startIndex);

    // Set last item as current on desktop only
    if (!this.isMobile && this.items.length > 0) {
      this.items[this.items.length - 1].isCurrent = true;
    }
  }

  @Method()
  async clear() {
    this.items = [];
  }

  @Method()
  async exportState() {
    return this.items.map((item) => ({
      label: item.label,
      isRoot: item.isRoot,
      level: item.level,
      isCurrent: item.isCurrent,
    }));
  }

  @Method()
  async importState(items: BreadcrumbItem[]) {
    // Clear existing items
    this.clear();

    // Import new items
    items.forEach((item) => {
      this.addItem(item.label, item.isRoot, item.level);
    });
  }

  render() {
    if (this.items.length === 0) return null;

    return (
      <nav
        class="breadcrumb"
        aria-label="Breadcrumb">
        <ol>
          {this.items.map((item) => (
            <li
              class={{
                'breadcrumb-item': true,
                current: item.isCurrent,
                'mobile-root-item': item.isRoot,
              }}>
              <dc-ui-header-nav-breadcrumb-item
                label={item.label}
                isCurrent={item.isCurrent}
                isRoot={item.isRoot}
                level={item.level}
                onClick={() =>
                  !item.isCurrent && this.handleClick(item.level, item.label, item.isRoot)
                }
              />
            </li>
          ))}
        </ol>
      </nav>
    );
  }
}
